import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Dialog from 'react-responsive-modal';
import { detect } from 'detect-browser';
import Loader from '../common/Loader';
import GetImage from '../common/GetImage';
import CheckMediaAccess from '../common/CheckMediaAccess';
import CheckNetworkConnection from '../common/CheckNetworkConnection';
import { clearNotification } from '../../action/NotificationAction';
import { soundAlert, connectVideoCall } from '../../action/SocketActions';
import { getVisitInformation } from '../../action/student/WaitingRoomAction';
import store from '../../../sessionStorage';
import { PATH } from '../../../utils/Constants';
import soundFile from '../../../utils/assets/notify.wav';
import DummyImage from '../../../utils/images/profileImg.png';
import { RESPONSE_CONSTANT } from '../../../utils/ResponseConstant';
import {
    Good,
    LevelTwoAdmin,
    Poor,
    Student,
    Tutor,
    Unknown,
} from '../../../utils/common/NamingTerms';
import LanguageContext, { LanguageProvider } from '../../../utils/common/LanguageContext';
import AppConfig from '../../../utils/common/DomainConfiguration';
import {
    pruneWhiteboardSessionsExcept,
    setActiveWhiteboardSession,
} from '../../../utils/WhiteboardSessionStorage';

const browser = detect();

const normalizeIncomingVisitType = (type) => {
    const normalizedType = String(type || '').toLowerCase();

    if (normalizedType === 'scheduled' || normalizedType === 'on-demand') {
        return 'one_to_one';
    }

    if (normalizedType === 'class_room_session') {
        return 'class_room';
    }

    return normalizedType;
};

const isClassroomRouteVisitType = (visitType) =>
    visitType === 'class_room' || visitType === 'conference_call';

// Higher-Order Component to inject translation function
const withLanguage = (WrappedComponent) => {
    return class extends Component {
        render() {
            return (
                <LanguageContext.Consumer>
                    {({ t }) => <WrappedComponent {...this.props} t={t} />}
                </LanguageContext.Consumer>
            );
        }
    };
};

/**
 * @package CareConnect
 * @type {React.ComponentClass}
 */
class IncomingCall extends Component {
    static propTypes = {
        clearNotification: PropTypes.func,
        notifications: PropTypes.object,
        history: PropTypes.object,
        getVisitInfo: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = {
            openDialog: false,
            checkPermissionStatus: false,
        };
        this.mediaChecker = new CheckMediaAccess();
        this.networkChecker = new CheckNetworkConnection();
        this.networkChecker.browser = browser.name;
    }

    /**
     * @inheritDoc
     */
    componentDidMount() {
        this.setState({ openDialog: false });
        sessionStorage.setItem('hasJoinedSession', 'false');
        this.intervalId = setInterval(this.checkUrlAndShowDialog, 100);
        window.addEventListener('popstate', this.handleBackNavigation);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    componentWillUnmount() {
        clearInterval(this.intervalId);
        window.removeEventListener('popstate', this.handleBackNavigation);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    handleBackNavigation = () => {
        this.setState({ openDialog: false });
    };

    handleVisibilityChange = () => {
        if (!document.hidden) {
            this.checkUrlAndShowDialog();
        }
    };

    checkUrlAndShowDialog = () => {
        try {
            const hasJoinedSession = sessionStorage.getItem('hasJoinedSession');
            const notificationType = this.props.notifications?.type;
            const isVonageMetadataOnly = notificationType === 'vonage_session_details';
            if (window.location.pathname.includes('video-call')) {
                sessionStorage.setItem('hasJoinedSession', 'true');
            }
            this.setState({
                openDialog:
                    !_.isEmpty(this.props.notifications) &&
                    !isVonageMetadataOnly &&
                    (hasJoinedSession === null || hasJoinedSession === 'false'),
            });
        } catch (e) {
            this.setState({
                openDialog: false,
            });
        }
    };

    /**
     * Navigates to video call screen
     */
    handleVideoCall = async (type) => {
    if (!type) {
        console.error('Type missing ❌');
        return;
    }

    const visitType = normalizeIncomingVisitType(type);

    sessionStorage.setItem('VISIT_TYPE', visitType);

    const { history, notifications, visitInformation } = this.props;

    const visitId =
        visitType === 'broadcast'
            ? notifications.id
            : notifications.visitId;

    setActiveWhiteboardSession(visitId);
    pruneWhiteboardSessionsExcept(visitId);

    const userId = notifications.patientUserId;
    const userSession = JSON.parse(sessionStorage.getItem('userSession'));

    this.props.getVisitInfo(visitId, (res) => {
        if (res.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
            connectVideoCall(res.response.activeSession);

            this.setState({ isLoading: false });
            soundAlert(0);
            sessionStorage.setItem('hasJoinedSession', 'true');

            if (userSession.userType === 'REGISTERED') {
                if (isClassroomRouteVisitType(visitType)) {
                    history.push(
                        `${PATH.IM_PATIENT.VIDEO_CALL}/${visitId}/${Student}/${store.get('userSession').id}/classroom`
                    );
                } else if (visitType === 'broadcast') {
                    if (store.get('insAdminSession')?.id) {
                        history.push(
                            `${PATH.LEVEL_TWO_ADMIN.VIDEO_CALL}/${visitId}/${LevelTwoAdmin}/${store.get('insAdminSession').id}/broadcast`
                        );
                    } else {
                        history.push(
                            `${PATH.IM_PATIENT.VIDEO_CALL}/${visitId}/${Student}/${store.get('userSession').id}/broadcast`
                        );
                    }
                } else {
                    history.push(
                        `${PATH.IM_PATIENT.VIDEO_CALL}/${visitId}/${Student}/${userId}`
                    );
                }
            } else if (
                userSession.userType === 'PHYSICIAN' ||
                userSession.userType === 'TUTOR'
            ) {
                if (isClassroomRouteVisitType(visitType)) {
                    history.push(
                        `${PATH.IM_PHYSICIAN.VIDEO_CALL}/${visitId}/${Tutor}/${store.get('userSession').id}/classroom`
                    );
                } else if (visitType === 'broadcast') {
                    history.push(
                        `${PATH.IM_PHYSICIAN.VIDEO_CALL}/${visitId}/${Tutor}/${store.get('userSession').id}/broadcast`
                    );
                } else {
                    history.push(
                        `${PATH.IM_PHYSICIAN.VIDEO_CALL}/${visitId}/${Tutor}/${store.get('userSession').id}/${userId}`
                    );
                }
            }

            this.checkUrlAndShowDialog();
        } else {
            connectVideoCall(visitInformation.activeSession);
        }

        this.setState({ checkPermissionStatus: false });
        this.props.clearNotification();
    });
};

    //preCallTest
    preCallTest = async () => {
        const { notifications } = this.props;
        try {
            this.setState({ checkPermissionStatus: true });
            await this.mediaChecker.finalCheckingPermission();
            if (
                this.mediaChecker.state.cameraGranted &&
                this.mediaChecker.state.microphoneGranted &&
                !this.mediaChecker.state.systemCameraBlocked &&
                !this.mediaChecker.state.systemMicrophoneBlocked
            ) {
                const networkGood =
                    this.networkChecker.browser === 'firefox' ||
                    this.networkChecker.browser === 'safari'
                        ? await this.networkChecker.checkfirefoxNetworkSignal()
                        : await this.networkChecker.checkNetworkSignal();

                if (
                    networkGood &&
                    (this.networkChecker.state.networkQuality === Good ||
                        this.networkChecker.state.networkQuality === Unknown)
                ) {
                    this.handleVideoCall(notifications.visitType);
                } else if (this.networkChecker.state.networkQuality === Poor) {
                    this.setState({ checkPermissionStatus: false });
                }
            } else {
                this.setState({ checkPermissionStatus: false });
            }
        } catch (error) {
            console.log('error: ', error);
        }
    };

    //closeIncomingModal
    closeIncomingModal = () => {
        soundAlert(0);
        this.props.clearNotification();
    };

    /**
     * Render Page
     */
    render() {
        const { notifications } = this.props;
        return (
            <div>
                <Dialog
                    onEscKeyDown={() => false}
                    onOverlayClick={() => false}
                    open={this.state.openDialog && !_.isEmpty(notifications)}
                    onClose={this.closeIncomingModal}
                    className={_.isEmpty(notifications) ? 'hide-dialog' : ''}
                    showCloseIcon={false}
                >
                    <audio id="player" autoPlay={!_.isEmpty(notifications) ? true : false} loop>
                        <source src={soundFile} type="audio/wav" />
                    </audio>
                    <div className="custom-close-button " onClick={this.closeIncomingModal}>
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
                                stroke="#B9B9B9"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div>
                        <p className="text-white">{this.props.t('incomingCall')}</p>
                    </div>
                    <div className="w-20 h-20 mx-auto mt-4 Incoming-call-img">
                        <img
                            src={
                                !_.isEmpty(notifications)
                                    ? sessionStorage.getItem('VISIT_TYPE') === 'broadcast' &&
                                      notifications.tutor?.image
                                        ? notifications.tutor?.image
                                        : notifications.profileImage
                                          ? notifications.profileImage
                                          : DummyImage
                                    : DummyImage
                            }
                            alt="physicianProfile"
                            className="h-full rounded-full"
                        />
                    </div>
                    {sessionStorage.getItem('VISIT_TYPE') === 'class_room' ||
                    sessionStorage.getItem('VISIT_TYPE') === 'broadcast' ? (
                        <div className="mt-6 text-center text-white">
                            {/* <p
                className="text-white"
                title={
                  !_.isEmpty(notifications) ? notifications.tutor?.name : ""
                }
              >
                {!_.isEmpty(notifications)
                  ? notifications.tutor?.name &&
                    notifications.tutor.name.length > 15
                    ? `${notifications.tutor.name.slice(0, 15)}...`
                    : notifications.tutor.name
                  : ""}
              </p> */}
                            {!_.isEmpty(notifications) &&
                            (notifications.tutor ||
                                notifications.channel ||
                                notifications.element) ? (
                                <div>
                                    {(notifications.tutor?.channelName ||
                                        notifications.channel) && (
                                        <p className="mb-1">
                                            <span className="font-medium">
                                                {`${AppConfig.labels.channel}`} :{' '}
                                            </span>
                                            {notifications.tutor?.channelName ||
                                                notifications.channel}
                                        </p>
                                    )}
                                    {(notifications.tutor?.elementName || notifications.element) &&
                                        (notifications.tutor?.elementName
                                            ? notifications.tutor.elementName.trim()
                                            : notifications.element.trim()) && (
                                            <p>
                                                <span className="font-medium">
                                                    {this.props.t('elementName')}:{' '}
                                                </span>
                                                {notifications.tutor?.elementName ||
                                                    notifications.element}
                                            </p>
                                        )}
                                </div>
                            ) : (
                                ''
                            )}
                        </div>
                    ) : (
                        <div className="mt-6 text-center text-white ">
                            {/* <p
                className="text-white"
                title={
                  !_.isEmpty(notifications) ? notifications.doctorName : ""
                }
              >
                {!_.isEmpty(notifications)
                  ? notifications.doctorName &&
                    notifications.doctorName.length > 15
                    ? `${notifications.doctorName.slice(0, 15)}...`
                    : notifications.doctorName
                  : ""}
              </p> */}
                            {!_.isEmpty(notifications) &&
                            (notifications.tutor ||
                                notifications.channel ||
                                notifications.element) ? (
                                <div>
                                    {(notifications.tutor?.channelName ||
                                        notifications.channel) && (
                                        <p className="mb-1">
                                            <span className="font-medium">
                                                {this.props.t(`${AppConfig.labels.channelName}`)}:{' '}
                                            </span>
                                            {notifications.tutor?.channelName ||
                                                notifications.channel}
                                        </p>
                                    )}
                                    {(notifications.tutor?.elementName || notifications.element) &&
                                        (notifications.tutor?.elementName
                                            ? notifications.tutor.elementName.trim()
                                            : notifications.element.trim()) && (
                                            <p>
                                                <span className="font-medium">
                                                    {this.props.t('elementName')}:{' '}
                                                </span>
                                                {notifications.tutor?.elementName ||
                                                    notifications.element}
                                            </p>
                                        )}
                                </div>
                            ) : (
                                ''
                            )}
                        </div>
                    )}
                    <div style={{ marginTop: '20px' }}>
                        <span className="flex items-center justify-center text-center mdl-chip mdl-chip--contact">
                            <GetImage className="w-2/3 mdl-chip__contact" isVideoLogo={true} />
                        </span>
                        <div className="flex items-center justify-center mt-6">
                            {this.state.checkPermissionStatus ? (
                                <Loader />
                            ) : (
                                <button
                                    className="bg-success p-3 py-1.5 rounded-lg text-white font-semibold whitespace-nowrap"
                                    onClick={this.preCallTest}
                                >
                                    {this.props.t('joinCall')}
                                </button>
                            )}
                        </div>
                    </div>
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        notifications: state.PushNotificationReducer.pushNotification,
    };
};

const mapDispatchToprops = (dispatch) => {
    return {
        clearNotification: () => {
            dispatch(clearNotification());
        },
        getVisitInfo: (visitId, callBack) => {
            dispatch(getVisitInformation(visitId, callBack));
        },
    };
};

const ConnectedIncomingCall = withLanguage(
    connect(mapStateToProps, mapDispatchToprops)(IncomingCall)
);

export default (props) => (
    <LanguageProvider>
        <ConnectedIncomingCall {...props} />
    </LanguageProvider>
);
